import { useEffect } from 'react'
import { Control, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form'

import { useTypedDispatch, useTypedSelector } from '../..'
import { options as optData } from '../../actions/create-defect'
import { clearDataByKey } from '../../reducers/createDefectSlice'
import { CreateDefectFormData, Equipment, PositionInVehicle } from './types'

/**
 * The position ("Vian sijainti") options for the selected (equipment, defect code). Keyed on a
 * useWatch, so the fetch re-runs on every path that changes either value: manual selection, an AI
 * suggestion applied via setValue, or a form reset.
 */
export const usePositionOptions = (
  control: Control<CreateDefectFormData>,
  getValues: UseFormGetValues<CreateDefectFormData>,
  setValue: UseFormSetValue<CreateDefectFormData>,
  selectedEquipment?: Equipment
): void => {
  const dispatch = useTypedDispatch()
  const defectCodePrefix = useWatch({ control, name: 'defectCodePrefix' }) ?? ''
  const serializedItemId = selectedEquipment?.itemId ?? ''
  const assistantDecidedNoPosition = useTypedSelector(
    (state) =>
      state.createDefectAssistant.body.vehiclePosition === '' &&
      state.createDefectAssistant.body.defectCode === defectCodePrefix
  )

  useEffect(() => {
    const keepPositionOnlyIfIn = (positions: PositionInVehicle[]) => {
      const selectedPosition = getValues('positionInVehicle')
      if (selectedPosition && !positions.some((p) => p.position === selectedPosition)) {
        setValue('positionInVehicle', '')
      }
    }

    if (!serializedItemId || !defectCodePrefix || assistantDecidedNoPosition) {
      dispatch(clearDataByKey('positions'))
      keepPositionOnlyIfIn([])
      return
    }

    const request = dispatch(
      optData.fetchPositionsActions({ serializedItemId, defectCode: defectCodePrefix })
    )
    void request.then((action) => {
      if (optData.fetchPositionsActions.fulfilled.match(action)) {
        keepPositionOnlyIfIn(action.payload.data)
      } else if (!action.meta.aborted) {
        keepPositionOnlyIfIn([])
      }
    })
    return () => request.abort()
  }, [
    dispatch,
    getValues,
    setValue,
    serializedItemId,
    defectCodePrefix,
    assistantDecidedNoPosition,
  ])
}
